import { useCallback, useEffect, useMemo, useState } from "react"
import { Gauge, Search } from "lucide-react"
import { useUserRoles, useToast } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { PageHeader, If, EmptyState } from "../../common/ui"
import {
  useFetchLeadScoresQuery,
  useAcknowledgeLeadScoresMutation
} from "../../redux/queries"
import { LeadScoreCategory, LeadScoreQueryParams } from "../../types"
import { getUserBrandId } from "../../common/utils"
import PaginationWrapper from "../../common/ui/pagination/wrapper"
import ScoreSummaryCards from "../../common/ui/lead-scoring/score-summary-cards"
import CategoryFilterBar from "../../common/ui/lead-scoring/category-filter-bar"
import type { AcknowledgedFilter } from "../../common/ui/lead-scoring/category-filter-bar"
import LeadScoreList from "../../common/ui/lead-scoring/lead-score-list"
import BulkActionToolbar from "../../common/ui/lead-scoring/bulk-action-toolbar"
import { CATEGORY_CONFIG } from "../../common/ui/lead-scoring/category-helpers"
import {
  USE_LEAD_SCORING_MOCK,
  getMockLeadScoringListResponse,
  getMockLeadScoringStatsResponse
} from "../../common/ui/lead-scoring/lead-scoring-mock"
import { cn } from "../../common/utils/classnames"

const PAGE_SIZE = 50

const LeadScoringPage = () => {
  const { user, isSuperAdmin, isBankAdmin, isLoanOfficer } = useUserRoles()
  const { pushSuccessToast, pushErrorToast } = useToast()
  const [activeCategory, setActiveCategory] =
    useState<LeadScoreCategory | null>(null)
  const [acknowledgedFilter, setAcknowledgedFilter] =
    useState<AcknowledgedFilter>("new")
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [optimisticAckedIds, setOptimisticAckedIds] = useState<Set<string>>(
    new Set()
  )

  const useMock = USE_LEAD_SCORING_MOCK

  const [acknowledgeScores, { isLoading: isAcknowledging }] =
    useAcknowledgeLeadScoresMutation()

  const baseParams = useMemo<LeadScoreQueryParams>(() => {
    const params: LeadScoreQueryParams = {}
    if (!isSuperAdmin && !isBankAdmin && isLoanOfficer && user?.id) {
      params.loid = user.id
    } else if (!isSuperAdmin && isBankAdmin && user?.id && user?.org) {
      const brandId =
        getUserBrandId(user.org, user.id) ??
        user.org?.brands?.[0]?.id ??
        user?.brand_id
      if (brandId) params.brand_id = brandId
    }
    return params
  }, [user, isSuperAdmin, isBankAdmin, isLoanOfficer])

  const { data: statsResponse } = useFetchLeadScoresQuery(
    { ...baseParams, limit: 1 },
    { skip: useMock }
  )

  const listParams = useMemo<LeadScoreQueryParams>(
    () => ({
      ...baseParams,
      ...(activeCategory ? { category: activeCategory } : {}),
      ...(acknowledgedFilter === "new"
        ? { acknowledged: 0 as const }
        : acknowledgedFilter === "reviewed"
        ? { acknowledged: 1 as const }
        : {}),
      page,
      limit: PAGE_SIZE
    }),
    [baseParams, activeCategory, acknowledgedFilter, page]
  )

  const { data: listResponse, isLoading } = useFetchLeadScoresQuery(
    listParams,
    { skip: useMock }
  )

  const mockStatsBundle = useMemo(
    () => (useMock ? getMockLeadScoringStatsResponse() : null),
    [useMock]
  )

  const mockListBundle = useMemo(
    () =>
      useMock
        ? getMockLeadScoringListResponse({
            activeCategory,
            acknowledgedFilter,
            page
          })
        : null,
    [useMock, activeCategory, acknowledgedFilter, page]
  )

  const scoresRaw = useMemo(() => {
    if (useMock) return mockListBundle?.data ?? []
    return listResponse?.data ?? []
  }, [useMock, mockListBundle, listResponse])

  const pagination = useMock
    ? mockListBundle?.pagination
    : listResponse?.pagination
  const stats = useMock ? mockStatsBundle?.stats : statsResponse?.stats

  const scores = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return scoresRaw
    return scoresRaw.filter(s => {
      const sub = s.subscription
      const name = `${sub.first_name} ${sub.last_name}`.toLowerCase()
      return name.includes(q) || sub.email.toLowerCase().includes(q)
    })
  }, [scoresRaw, searchQuery])

  useEffect(() => {
    setSelectedIds(new Set())
    setOptimisticAckedIds(new Set())
  }, [activeCategory, acknowledgedFilter, page])

  const handleCategoryChange = useCallback(
    (category: LeadScoreCategory | null) => {
      setActiveCategory(category)
      setPage(1)
    },
    []
  )

  const handleAcknowledgedFilterChange = useCallback(
    (filter: AcknowledgedFilter) => {
      setAcknowledgedFilter(filter)
      setPage(1)
    },
    []
  )

  const handleAcknowledge = useCallback(
    async (ids: string[]) => {
      if (useMock) return
      setOptimisticAckedIds(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.add(id))
        return next
      })
      setSelectedIds(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.delete(id))
        return next
      })

      try {
        await acknowledgeScores({ ids }).unwrap()
        pushSuccessToast(
          `${ids.length} lead${ids.length > 1 ? "s" : ""} marked as reviewed`
        )
      } catch {
        setOptimisticAckedIds(prev => {
          const next = new Set(prev)
          ids.forEach(id => next.delete(id))
          return next
        })
        pushErrorToast("Failed to update — please try again")
      }
    },
    [useMock, acknowledgeScores, pushSuccessToast, pushErrorToast]
  )

  const handleUndoAcknowledge = useCallback(
    async (ids: string[]) => {
      if (useMock) return
      try {
        await acknowledgeScores({ ids, undo: true }).unwrap()
        pushSuccessToast(
          `${ids.length} lead${ids.length > 1 ? "s" : ""} marked as new`
        )
      } catch {
        pushErrorToast("Failed to update — please try again")
      }
    },
    [useMock, acknowledgeScores, pushSuccessToast, pushErrorToast]
  )

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(scores.map(s => s.id)))
  }, [scores])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const pageIndex = page - 1
  const totalPages = pagination?.total_pages ?? 1

  const listLoading = useMock ? false : isLoading

  const sectionTitle = activeCategory
    ? CATEGORY_CONFIG[activeCategory].labelPlural
    : "All leads"

  const emptyFromFilters = !listLoading && scoresRaw.length === 0

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["lead-scoring"]}>
      <div className="min-h-full bg-white p-6">
        <PageHeader
          title={
            <span className="flex items-center gap-3 text-2xl font-medium text-[#232227]">
              Scoreboard
              {stats?.unacknowledged != null && stats.unacknowledged > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-pulse-glow absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-400" />
                  </span>
                  <span className="text-xs font-bold text-primary-600">
                    {stats.unacknowledged} new
                  </span>
                </span>
              )}
            </span>
          }
          hasSubtitle
          subtitle="AI-powered intent scoring for your leads"
        />

        <div className="mt-6 space-y-5">
          <ScoreSummaryCards
            stats={stats}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryChange}
          />

          <CategoryFilterBar
            acknowledgedFilter={acknowledgedFilter}
            onAcknowledgedFilterChange={handleAcknowledgedFilterChange}
            unacknowledgedCount={stats?.unacknowledged}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h2 className="text-xl font-bold text-[#151c27]">{sectionTitle}</h2>
            <div
              className={cn(
                "flex h-[46px] w-full max-w-md items-center gap-2 rounded border border-[#e4e4e4] bg-white px-3",
                "sm:shrink-0"
              )}
            >
              <Search className="h-5 w-5 shrink-0 text-[#a3a3a9]" aria-hidden />
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search leads"
                className="min-w-0 flex-1 border-0 bg-transparent text-sm text-[#232227] placeholder:text-[#a3a3a9] focus:outline-none focus:ring-0"
                aria-label="Search leads"
              />
            </div>
          </div>

          <If
            condition={!emptyFromFilters}
            isLoading={listLoading}
            then={
              scores.length > 0 ? (
                <LeadScoreList
                  scores={scores}
                  selectedIds={selectedIds}
                  optimisticAckedIds={optimisticAckedIds}
                  onToggleSelect={useMock ? undefined : handleToggleSelect}
                  onSelectAll={useMock ? undefined : handleSelectAll}
                  onClearSelection={useMock ? undefined : handleClearSelection}
                  onAcknowledge={
                    useMock ? undefined : id => handleAcknowledge([id])
                  }
                  onUndoAcknowledge={
                    useMock ? undefined : id => handleUndoAcknowledge([id])
                  }
                />
              ) : (
                <EmptyState
                  icon={Gauge}
                  heading="No matching leads"
                  subHeading="Try a different search or clear the search box."
                />
              )
            }
            else={
              <EmptyState
                icon={Gauge}
                heading={
                  acknowledgedFilter === "new"
                    ? "All caught up"
                    : "No leads found"
                }
                subHeading={
                  acknowledgedFilter === "new"
                    ? "No new scores to review — check back later"
                    : "Try adjusting your filters or check back later"
                }
              />
            }
          />

          {totalPages > 1 && !useMock && (
            <PaginationWrapper
              pageIndex={pageIndex}
              pageSize={PAGE_SIZE}
              rowCount={pagination?.total ?? 0}
              totalCount={totalPages}
              disabledPrev={page <= 1}
              disabledNext={page >= totalPages}
              onClickPrev={() => setPage(p => Math.max(1, p - 1))}
              onClickNext={() => setPage(p => Math.min(totalPages, p + 1))}
              setPageIndex={idx => setPage(idx + 1)}
            />
          )}
        </div>

        {!useMock && selectedIds.size > 0 && (
          <BulkActionToolbar
            selectedCount={selectedIds.size}
            totalCount={scores.length}
            onAcknowledge={() => handleAcknowledge(Array.from(selectedIds))}
            onUndoAcknowledge={() =>
              handleUndoAcknowledge(Array.from(selectedIds))
            }
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            isAcknowledging={isAcknowledging}
            showUndo={acknowledgedFilter === "reviewed"}
          />
        )}
      </div>
    </RequireRole>
  )
}

export default LeadScoringPage
